import { Box, Button, Modal, TextInput, SelectInput, toast, Alert } from '@palmetto/palmetto-components';
import { FormikValues, useFormik } from 'formik';
import { SyntheticEvent } from 'react';
import * as yup from 'yup';

import { FundType, FundTypesLabels } from 'types';

import { useCreateFundMutation } from '../../services/funds';

export const CreateFundModal = ({
  isModalOpen,
  closeModal,
}: {
  isModalOpen: boolean;
  closeModal: (event?: SyntheticEvent) => void;
}) => {
  const [createFund] = useCreateFundMutation();

  const fundTypeOptions = Object.entries(FundTypesLabels).map(([value, label]) => ({
    value,
    label,
  }));

  const formik = useFormik({
    initialValues: {
      name: '',
      fundType: '',
      concordLenderCode: '',
      netsuiteId: '',
      commitmentExpirationDate: '',
      fundCompletionDate: '',
    },
    validationSchema: yup.object({
      name: yup.string().required('Fund name is required').max(30, 'Fund name must be 30 characters or fewer'),
      fundType: yup.string().required('Fund type is required'),
      concordLenderCode: yup
        .string()
        .required('Concord Lender Code is required')
        .matches(
          /^([A-Za-z]{2}\d{1}|[A-Za-z]{1}\d{2})$/,
          'Concord Lender Code must be either 2 alpha characters followed by 1 numeric character (e.g., "AB1") or 1 alpha character followed by 2 numeric characters (e.g., "L03")',
        ),
      netsuiteId: yup.string().required('Netsuite ID is required'),
      commitmentExpirationDate: yup.string().required('Commitment Expiration Date is required'),
      fundCompletionDate: yup.string().required('Fund Completion Date is required'),
    }),
    onSubmit: async (values: FormikValues, { setSubmitting, resetForm }) => {
      try {
        await createFund({
          name: values.name,
          fundType: values.fundType as FundType,
          concordLenderCode: values.concordLenderCode,
          netsuiteId: values.netsuiteId,
          commitmentExpirationDate: values.commitmentExpirationDate,
          fundCompletionDate: values.fundCompletionDate,
        }).unwrap();

        toast.success('Fund created successfully');
        resetForm();
        closeModal();
      } catch (error: any) {
        console.error('Error creating fund:', error);
        toast.error(error?.data?.message || 'Error creating fund');
      } finally {
        setSubmitting(false);
      }
    },
  });

  return (
    <Modal ariaLabelledBy="createFund" isOpen={isModalOpen} onDismiss={closeModal} maxWidth="500px">
      <Modal.Header id="createFund" title="Create New Fund" onDismiss={closeModal} />
      <form onSubmit={formik.handleSubmit}>
        <Modal.Body background="secondary">
          <Box direction="column" childGap="md">
            <Alert
              variant="warning"
              title="Data Accuracy Required"
              message="Please ensure all fund information is accurate and is already in Concord and NetSuite systems. Incorrect data will cause system errors and integration issues."
            />
            <TextInput
              type="text"
              label="Fund Name"
              name="name"
              id="name"
              value={formik.values.name}
              onChange={(event) => formik.setFieldValue('name', event.target.value)}
              onBlur={formik.handleBlur}
              error={formik.touched.name && (formik.errors.name as string)}
              isRequired
            />

            <SelectInput
              label="Fund Type"
              name="fundType"
              id="fundType"
              value={
                fundTypeOptions.find((option) => option.value === formik.values.fundType) || {
                  value: '',
                  label: 'Select fund type...',
                }
              }
              onChange={(event) => formik.setFieldValue('fundType', event.target.value.value)}
              onBlur={formik.handleBlur}
              error={formik.touched.fundType && (formik.errors.fundType as string)}
              isRequired
              options={[{ value: '', label: 'Select fund type...' }, ...fundTypeOptions]}
            />

            <TextInput
              type="text"
              label="Concord Lender Code"
              name="concordLenderCode"
              id="concordLenderCode"
              value={formik.values.concordLenderCode}
              onChange={(event) => formik.setFieldValue('concordLenderCode', event.target.value)}
              onBlur={formik.handleBlur}
              error={formik.touched.concordLenderCode && (formik.errors.concordLenderCode as string)}
              isRequired
            />

            <TextInput
              type="text"
              label="Netsuite ID"
              name="netsuiteId"
              id="netsuiteId"
              value={formik.values.netsuiteId}
              onChange={(event) => formik.setFieldValue('netsuiteId', event.target.value)}
              onBlur={formik.handleBlur}
              error={formik.touched.netsuiteId && (formik.errors.netsuiteId as string)}
              isRequired
            />

            <TextInput
              type="text"
              label="Commitment Expiration Date"
              name="commitmentExpirationDate"
              id="commitmentExpirationDate"
              value={formik.values.commitmentExpirationDate}
              placeholder="MM-DD-YYYY"
              inputMask="date"
              onChange={(event) => formik.setFieldValue('commitmentExpirationDate', event.target.value)}
              onBlur={formik.handleBlur}
              error={formik.touched.commitmentExpirationDate && (formik.errors.commitmentExpirationDate as string)}
              isRequired
            />

            <TextInput
              type="text"
              label="Fund Completion Date"
              name="fundCompletionDate"
              id="fundCompletionDate"
              value={formik.values.fundCompletionDate}
              placeholder="MM-DD-YYYY"
              inputMask="date"
              onChange={(event) => formik.setFieldValue('fundCompletionDate', event.target.value)}
              onBlur={formik.handleBlur}
              error={formik.touched.fundCompletionDate && (formik.errors.fundCompletionDate as string)}
              isRequired
            />
          </Box>
        </Modal.Body>
        <Modal.Footer>
          <Button
            as="button"
            onClick={() => {
              formik.resetForm();
              closeModal();
            }}
            variant="secondary"
            tone="neutral"
            size="md"
            isLoading={formik.isSubmitting}
          >
            Cancel
          </Button>
          <Button variant="primary" size="md" type="submit" isLoading={formik.isSubmitting}>
            Create Fund
          </Button>
        </Modal.Footer>
      </form>
    </Modal>
  );
};
